import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { globalAgentsApi, GlobalCallCenterAgent } from '../services/api'
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Select,
  Space,
  Row,
  Col,
  Typography,
  Alert,
  Tag,
  Table,
  Modal,
  Popconfirm,
  Spin
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons'

const { Title, Text } = Typography
const { Option } = Select

const availableSkills = [
  'voice',
  'chat',
  'sms',
  'callback',
  'sales',
  'support',
  'billing',
  'technical',
  'spanish',
  'priority'
]

const GlobalAgentsPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [modalVisible, setModalVisible] = useState(false)
  const [editingAgent, setEditingAgent] = useState<GlobalCallCenterAgent | null>(null)
  const [form] = Form.useForm()

  const { data: agents, isLoading } = useQuery('globalAgents', globalAgentsApi.list)

  const createMutation = useMutation(globalAgentsApi.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('globalAgents')
      setModalVisible(false)
      form.resetFields()
      toast.success('Agent created successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create agent')
    },
  })

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<GlobalCallCenterAgent> }) =>
      globalAgentsApi.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('globalAgents')
        setModalVisible(false)
        setEditingAgent(null)
        form.resetFields()
        toast.success('Agent updated successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to update agent')
      },
    }
  )

  const deleteMutation = useMutation(globalAgentsApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('globalAgents')
      toast.success('Agent deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete agent')
    },
  })

  const toggleActiveMutation = useMutation(globalAgentsApi.toggleActive, {
    onSuccess: () => {
      queryClient.invalidateQueries('globalAgents')
      toast.success('Agent status updated')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update status')
    },
  })

  const handleSubmit = (values: any) => {
    if (editingAgent) {
      updateMutation.mutate({ id: editingAgent.id, data: values })
    } else {
      createMutation.mutate(values)
    }
  }

  const handleEdit = (agent: GlobalCallCenterAgent) => {
    setEditingAgent(agent)
    form.setFieldsValue({
      firstName: agent.firstName,
      lastName: agent.lastName,
      email: agent.email,
      phone: agent.phone || '',
      skills: agent.skills || [],
      priority: agent.priority,
      isActive: agent.isActive,
    })
    setModalVisible(true)
  }

  const handleAdd = () => {
    setEditingAgent(null)
    form.resetFields()
    form.setFieldsValue({
      priority: 5,
      isActive: true,
      skills: []
    })
    setModalVisible(true)
  }

  const handleCancel = () => {
    setModalVisible(false)
    setEditingAgent(null)
    form.resetFields()
  }

  const getPriorityLabel = (priority: number) => {
    if (priority <= 2) return { text: 'High', color: 'green' }
    if (priority <= 5) return { text: 'Normal', color: 'blue' }
    return { text: 'Low', color: 'default' }
  }

  const columns = [
    {
      title: 'Agent',
      key: 'agent',
      render: (record: GlobalCallCenterAgent) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</div>
          <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
            <MailOutlined style={{ marginRight: 4 }} />
            {record.email}
          </div>
          {record.phone && (
            <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {record.phone}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Skills',
      key: 'skills',
      render: (record: GlobalCallCenterAgent) => (
        <Space wrap>
          {record.skills?.length > 0 ? (
            record.skills.map(skill => (
              <Tag key={skill} color="blue">{skill}</Tag>
            ))
          ) : (
            <Text type="secondary">No skills</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Priority',
      key: 'priority',
      render: (record: GlobalCallCenterAgent) => {
        const { text, color } = getPriorityLabel(record.priority)
        return <Tag color={color}>{record.priority} - {text}</Tag>
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: GlobalCallCenterAgent) => (
        <Switch
          checked={record.isActive}
          onChange={() => toggleActiveMutation.mutate(record.id)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: GlobalCallCenterAgent) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete Agent"
            description={`Are you sure you want to delete ${record.firstName} ${record.lastName}?`}
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <UserOutlined style={{ marginRight: 8 }} />
          Global Call Center Agents
        </Title>
        <Text type="secondary">
          Manage dedicated platform-wide call center agents who can handle calls across all businesses
        </Text>
      </div>

      <Card>
        <Alert
          message="Global Agents"
          description="These agents are platform-wide and can be assigned to handle calls for any business. They are separate from business-specific office staff."
          type="info"
          style={{ marginBottom: '16px' }}
        />

        <div style={{ marginBottom: '16px' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Agent
          </Button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={agents}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            locale={{
              emptyText: (
                <div style={{ padding: '48px' }}>
                  <UserOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                  <div style={{ marginTop: '16px', color: '#8c8c8c' }}>
                    No global agents yet. Click "Add Agent" to create one.
                  </div>
                </div>
              )
            }}
          />
        )}
      </Card>

      <Modal
        title={editingAgent ? 'Edit Agent' : 'Add New Agent'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ priority: 5, isActive: true, skills: [] }}
        >
          <Alert
            message="Agent Information"
            description="Enter the agent's contact information and configure their call handling settings."
            type="info"
            style={{ marginBottom: '16px' }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'First name is required' }]}
              >
                <Input placeholder="John" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Last name is required' }]}
              >
                <Input placeholder="Doe" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input placeholder="john.doe@example.com" disabled={!!editingAgent} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone Number"
              >
                <Input placeholder="(555) 123-4567" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="skills"
            label="Skills"
          >
            <Select
              mode="multiple"
              placeholder="Select skills"
              style={{ width: '100%' }}
            >
              {availableSkills.map(skill => (
                <Option key={skill} value={skill}>{skill}</Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priority (0 = Highest, 10 = Lowest)"
              >
                <Select>
                  <Option value={0}>0 - Highest Priority</Option>
                  <Option value={1}>1</Option>
                  <Option value={2}>2</Option>
                  <Option value={3}>3</Option>
                  <Option value={5}>5 - Normal</Option>
                  <Option value={7}>7</Option>
                  <Option value={10}>10 - Low Priority</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="Status"
                valuePropName="checked"
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: 'right', marginTop: '16px' }}>
            <Space>
              <Button onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={createMutation.isLoading || updateMutation.isLoading}
              >
                {editingAgent ? 'Update' : 'Create'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default GlobalAgentsPage
